/**
 * Control Plugin Validator
 * Validates control plugin configuration before registration
 */

import type { ControlPlugin, FormControlType } from '@atlas-forms/types-js';
import { ValidationError, ValidationErrorDetail } from './validation-error';

/**
 * Valid icon names for controls
 * Based on common icon libraries (Material Design, Font Awesome, etc.)
 */
const VALID_ICON_NAMES = new Set([
  'text-icon',
  'textarea-icon',
  'number-icon',
  'email-icon',
  'password-icon',
  'checkbox-icon',
  'radio-icon',
  'select-icon',
  'date-icon',
  'image-icon',
  'button-icon',
  'link-icon',
  'table-icon',
  'chart-icon',
  'file-icon',
  'custom-icon',
  // Allow custom SVG strings
  'svg',
]);

/**
 * Standard validation rule names
 */
const STANDARD_VALIDATORS = new Set([
  'required',
  'minLength',
  'maxLength',
  'min',
  'max',
  'pattern',
  'email',
  'url',
  'date',
  'custom',
]);

/**
 * Known control categories for classification
 */
const KNOWN_CATEGORIES = new Set([
  'Input',
  'Display',
  'Layout',
  'Chart',
  'Media',
  'Advanced',
  'Custom',
  'Other',
]);

/**
 * Control Plugin Validator
 * Provides comprehensive validation for control plugins before registration
 */
export class ControlValidator {
  /**
   * Validate a complete control plugin
   * @param plugin The control plugin to validate
   * @returns true if valid, throws ValidationError if invalid
   * @throws ValidationError if validation fails
   */
  static validateControl(plugin: ControlPlugin): boolean {
    const details: ValidationErrorDetail[] = [];

    // Validate required fields
    this.validateRequiredFields(plugin, details);

    // If critical validation failed, throw early
    if (details.some((d) => d.severity === 'error' && d.code === 'missing-required')) {
      throw new ValidationError(
        'Control plugin is missing required fields',
        details,
        plugin.type
      );
    }

    // Validate individual fields
    this.validateControlType(plugin.type, details);
    this.validateLabel(plugin.label, details);
    this.validateGroup(plugin.group ?? plugin.category, details);

    // Optional field validations
    if (plugin.icon) {
      this.validateIcon(plugin.icon, details);
    }
    if (plugin.description) {
      this.validateDescription(plugin.description, details);
    }
    if (plugin.defaultConfig) {
      this.validateDefaultConfig(plugin.defaultConfig, details);
    }

    // If any critical errors, throw
    if (details.some((d) => d.severity === 'error')) {
      throw new ValidationError(
        `Control plugin validation failed for type '${plugin.type}'`,
        details,
        plugin.type
      );
    }

    return true;
  }

  /**
   * Validate control type naming
   * Must be alphanumeric with hyphens only
   * @param type The control type to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateControlType(
    type: FormControlType | string,
    details?: ValidationErrorDetail[]
  ): boolean {
    const typeStr = String(type);
    const detailsArr = details || [];

    // Check if type is provided
    if (!typeStr || typeStr.trim() === '') {
      detailsArr.push({
        field: 'type',
        message: 'Control type cannot be empty',
        severity: 'error',
        code: 'empty-type',
      });
      return false;
    }

    // Check naming pattern: alphanumeric and hyphens only
    const validPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;
    if (!validPattern.test(typeStr)) {
      detailsArr.push({
        field: 'type',
        message: `Control type '${typeStr}' contains invalid characters. Use alphanumeric and hyphens (e.g., 'text-input', 'custom-control').`,
        severity: 'error',
        code: 'invalid-type-format',
      });
      return false;
    }

    // Check length
    if (typeStr.length > 64) {
      detailsArr.push({
        field: 'type',
        message: `Control type must be 64 characters or less. Current length: ${typeStr.length}`,
        severity: 'error',
        code: 'type-too-long',
      });
      return false;
    }

    return true;
  }

  /**
   * Validate control label
   * Must be a non-empty string
   * @param label The label to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateLabel(
    label: string | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (!label || typeof label !== 'string' || label.trim() === '') {
      detailsArr.push({
        field: 'label',
        message: 'Control label is required and must be a non-empty string',
        severity: 'error',
        code: 'invalid-label',
      });
      return false;
    }

    if (label.length > 256) {
      detailsArr.push({
        field: 'label',
        message: `Control label must be 256 characters or less. Current length: ${label.length}`,
        severity: 'warning',
        code: 'label-too-long',
      });
    }

    return true;
  }

  /**
   * Validate control group
   * Must be a non-empty string
   * @param group The group to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateGroup(
    group: string | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (!group || typeof group !== 'string' || group.trim() === '') {
      detailsArr.push({
        field: 'group',
        message: 'Control group is required and must be a non-empty string',
        severity: 'error',
        code: 'invalid-group',
      });
      return false;
    }

    if (group.length > 128) {
      detailsArr.push({
        field: 'group',
        message: `Control group must be 128 characters or less. Current length: ${group.length}`,
        severity: 'warning',
        code: 'group-too-long',
      });
    }

    if (!KNOWN_CATEGORIES.has(group)) {
      detailsArr.push({
        field: 'group',
        message: `Control group '${group}' is not a standard category. Known categories: ${Array.from(KNOWN_CATEGORIES).join(', ')}`,
        severity: 'warning',
        code: 'unknown-group',
      });
    }

    return true;
  }

  /**
   * Validate control icon
   * Must be a valid icon name or SVG string
   * @param icon The icon to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateIcon(
    icon: string | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (!icon || typeof icon !== 'string') {
      detailsArr.push({
        field: 'icon',
        message: 'Control icon must be a non-empty string',
        severity: 'error',
        code: 'invalid-icon',
      });
      return false;
    }

    // Allow SVG strings or common icon names
    const isSvg = icon.startsWith('<svg') || icon.includes('icon');
    if (!isSvg && !VALID_ICON_NAMES.has(icon)) {
      detailsArr.push({
        field: 'icon',
        message: `Icon '${icon}' is not a recognized icon name. Use standard icon names or valid SVG strings.`,
        severity: 'warning',
        code: 'unknown-icon',
      });
    }

    return true;
  }

  /**
   * Validate control description
   * Must be a non-empty string if provided
   * @param description The description to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateDescription(
    description: string | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (description !== undefined && typeof description !== 'string') {
      detailsArr.push({
        field: 'description',
        message: 'Control description must be a string if provided',
        severity: 'error',
        code: 'invalid-description-type',
      });
      return false;
    }

    if (description && description.trim() === '') {
      detailsArr.push({
        field: 'description',
        message: 'Control description cannot be empty',
        severity: 'error',
        code: 'empty-description',
      });
      return false;
    }

    if (description && description.length > 1024) {
      detailsArr.push({
        field: 'description',
        message: `Control description must be 1024 characters or less. Current length: ${description.length}`,
        severity: 'warning',
        code: 'description-too-long',
      });
    }

    return true;
  }

  /**
   * Validate default configuration is JSON-serializable
   * @param config The default config to validate
   * @param details Error details collector
   * @returns true if valid
   */
  static validateDefaultConfig(
    config: Record<string, unknown> | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (config === undefined || config === null) {
      return true;
    }

    if (typeof config !== 'object' || Array.isArray(config)) {
      detailsArr.push({
        field: 'defaultConfig',
        message: 'Default config must be an object (not an array)',
        severity: 'error',
        code: 'invalid-config-type',
      });
      return false;
    }

    // Test JSON serializability
    try {
      JSON.stringify(config);
    } catch (error) {
      detailsArr.push({
        field: 'defaultConfig',
        message: `Default config is not JSON-serializable: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
        code: 'non-serializable-config',
      });
      return false;
    }

    // Warn if config is too large
    const configSize = JSON.stringify(config).length;
    if (configSize > 10000) {
      detailsArr.push({
        field: 'defaultConfig',
        message: `Default config is quite large (${configSize} bytes). Consider simplifying it.`,
        severity: 'warning',
        code: 'large-config',
      });
    }

    return true;
  }

  /**
   * Validate supported validation rule names
   * Checks if validators reference actual validation rules
   * @param validations Array of validator names to check
   * @param details Error details collector
   * @returns true if valid
   */
  static validateSupportedValidations(
    validations: string[] | undefined,
    details?: ValidationErrorDetail[]
  ): boolean {
    const detailsArr = details || [];

    if (!validations) {
      return true;
    }

    if (!Array.isArray(validations)) {
      detailsArr.push({
        field: 'supportedValidations',
        message: 'Supported validations must be an array of strings',
        severity: 'error',
        code: 'invalid-validations-type',
      });
      return false;
    }

    const unknownValidators: string[] = [];
    for (const validator of validations) {
      if (typeof validator !== 'string') {
        detailsArr.push({
          field: 'supportedValidations',
          message: `Validator name must be a string, got ${typeof validator}`,
          severity: 'error',
          code: 'invalid-validator-type',
        });
      } else if (!STANDARD_VALIDATORS.has(validator)) {
        unknownValidators.push(validator);
      }
    }

    if (unknownValidators.length > 0) {
      detailsArr.push({
        field: 'supportedValidations',
        message: `Unknown validators: ${unknownValidators.join(', ')}. Standard validators are: ${Array.from(STANDARD_VALIDATORS).join(', ')}`,
        severity: 'warning',
        code: 'unknown-validators',
      });
    }

    return true;
  }

  /**
   * Validate required fields are present
   * @param plugin The plugin to check
   * @param details Error details collector
   */
  private static validateRequiredFields(
    plugin: ControlPlugin,
    details: ValidationErrorDetail[]
  ): void {
    const missing: string[] = [];

    if (!plugin.type) missing.push('type');
    if (!plugin.label) missing.push('label');
    // Accept either `group` or `category` — both are valid per ControlPlugin type
    if (!plugin.group && !plugin.category) missing.push('group');

    if (missing.length > 0) {
      details.push({
        field: 'required-fields',
        message: `Control must have required fields: type, label, group (or category). Missing: ${missing.join(', ')}`,
        severity: 'error',
        code: 'missing-required',
      });
    }
  }
}
